import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "./api/client";
import AccountSettingSelect from "./components/AccountSettingSelect";
import SettingsSavedNotice from "./components/SettingsSavedNotice";
import { useCurrentUser } from "./currentUserContext";
import {
  DEFAULT_PAYMENT_CURRENCY,
  SUPPORTED_PAYMENT_CURRENCIES,
  normalizePaymentCurrencyCode
} from "./utils/pricingCurrency";

export default function PricingCurrencyView() {
  const { selectedUserId } = useCurrentUser();
  const [savedSettings, setSavedSettings] = useState({
    paymentConversionEnabled: false,
    paymentCurrencyCode: DEFAULT_PAYMENT_CURRENCY
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [settings, setSettings] = useState({
    paymentConversionEnabled: false,
    paymentCurrencyCode: DEFAULT_PAYMENT_CURRENCY
  });

  useEffect(() => {
    let isDisposed = false;

    async function loadSettings() {
      try {
        setLoading(true);
        const response = await apiFetch(`/api/account/pricing-currency-settings/${selectedUserId}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load Pricing and Currency settings");
        }

        if (isDisposed) {
          return;
        }

        const nextSettings = {
          paymentConversionEnabled: Boolean(data?.settings?.paymentConversionEnabled),
          paymentCurrencyCode: normalizePaymentCurrencyCode(data?.settings?.paymentCurrencyCode)
        };

        setSavedSettings(nextSettings);
        setSettings(nextSettings);
        setExchangeRates(data?.exchangeRates || null);
        setError(null);
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Pricing currency settings load error:", loadError);
          setError(loadError.message || "Could not load Pricing and Currency settings");
        }
      } finally {
        if (!isDisposed) {
          setLoading(false);
        }
      }
    }

    loadSettings();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId]);

  const currentRate = Number(exchangeRates?.rates?.[settings.paymentCurrencyCode]);
  const isDirty = settings.paymentConversionEnabled !== savedSettings.paymentConversionEnabled
    || settings.paymentCurrencyCode !== savedSettings.paymentCurrencyCode;

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setNotice(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    if (loading || saving || !isDirty) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setSaving(true);
        setError(null);
        setNotice(null);

        const response = await apiFetch(`/api/account/pricing-currency-settings/${selectedUserId}`, {
          userId: selectedUserId,
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(settings)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not save Pricing and Currency settings");
        }

        const nextSettings = {
          paymentConversionEnabled: Boolean(data?.settings?.paymentConversionEnabled),
          paymentCurrencyCode: normalizePaymentCurrencyCode(data?.settings?.paymentCurrencyCode)
        };

        setSavedSettings(nextSettings);
        setSettings(nextSettings);
        setNotice("Settings saved");
      } catch (saveError) {
        console.error("Pricing currency settings save error:", saveError);
        setError(saveError.message || "Could not save Pricing and Currency settings");
      } finally {
        setSaving(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [isDirty, loading, saving, selectedUserId, settings]);

  return (
    <div className="account-shell">
      <div className="account-settings-page">
        <div className="account-breadcrumb">
          <Link to="/account">My Account</Link>
          <span>/</span>
          <span>Pricing and Currency</span>
        </div>

        <div className="account-settings-panel__header">
          <div>
            <h1 className="account-title">Pricing and Currency</h1>
            <p className="account-copy" style={{ marginTop: "8px", maxWidth: "760px" }}>
              Choose whether TradeFinder should show a converted payment amount next to the base EUR payment.
            </p>
          </div>
        </div>

        <section
          className="account-setting-card account-setting-card--wide"
          style={{
            maxWidth: "880px"
          }}
        >
          <div className="account-setting-card__header account-setting-card__header--stacked">
            <div>
              <h2 className="account-setting-card__title">TradeFinder payment conversion</h2>
              <p className="account-setting-card__copy" style={{ maxWidth: "720px" }}>
                When enabled, TradeFinder shows payment values as the original EUR amount plus a live conversion in your selected currency.
              </p>
            </div>
            <label className="account-toggle">
              <input
                type="checkbox"
                checked={settings.paymentConversionEnabled}
                onChange={(event) => {
                  setSettings((current) => ({
                    ...current,
                    paymentConversionEnabled: event.target.checked
                  }));
                }}
              />
              <span className="account-toggle__switch" aria-hidden="true" />
              <span className="account-toggle__label">{settings.paymentConversionEnabled ? "On" : "Off"}</span>
            </label>
          </div>

          <div className="account-setting-card__body" style={{ gap: "16px" }}>
            <div className="account-setting-card__value">
              {settings.paymentConversionEnabled ? `Show converted payment in ${settings.paymentCurrencyCode}` : "Off"}
            </div>

            <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
              <label className="account-location-card__label" style={{ minWidth: 0, width: "min(320px, 100%)", flex: "1 1 280px" }}>
              Payment currency
                <div style={{ marginTop: "8px" }}>
                  <AccountSettingSelect
                    title="Payment currency"
                    value={settings.paymentCurrencyCode}
                    options={SUPPORTED_PAYMENT_CURRENCIES}
                    onChange={(nextValue) => {
                      setSettings((current) => ({
                        ...current,
                        paymentCurrencyCode: normalizePaymentCurrencyCode(nextValue)
                      }));
                    }}
                  />
                </div>
              </label>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px", color: "#475569", fontSize: "14px", lineHeight: 1.6 }}>
            <div>
              Preview: <strong style={{ color: "#0f172a" }}>Payment: 12.00 €{settings.paymentConversionEnabled && Number.isFinite(currentRate) ? ` (${(12 * currentRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${settings.paymentCurrencyCode})` : ""}</strong>
            </div>
            <div>
              {Number.isFinite(currentRate)
                ? `Latest rate: 1 EUR = ${currentRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${settings.paymentCurrencyCode}`
                : "Live exchange rates are currently unavailable. TradeFinder will keep showing EUR only until rates can be loaded again."}
            </div>
            {exchangeRates?.fetchedAt && (
              <div>Rate fetched: {new Date(exchangeRates.fetchedAt).toLocaleString()}</div>
            )}
          </div>

          {loading && <div className="account-loading">Loading settings...</div>}
          {error && <div className="account-error">{error}</div>}
          </div>
        </section>
      </div>

      {notice ? <SettingsSavedNotice message={notice} /> : null}
    </div>
  );
}